import QRCodeStyling from 'qr-code-styling';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';

// Универсальный парсер ссылок
export const parseUrls = (text) => {
  if (!text) return [];
  const chunks = text.split(/(?=https?:\/\/)/i);
  const results = [];

  chunks.forEach((chunk) => {
    const urlStr = chunk.trim();
    if (!urlStr.toLowerCase().startsWith('http')) return;

    try {
      const cleanUrl = urlStr.match(/https?:\/\/[^\s]+/i)?.[0] || urlStr;
      const urlObj = new URL(cleanUrl);
      const params = Object.fromEntries(urlObj.searchParams.entries());
      
      if (Object.keys(params).length > 0) {
        results.push({ url: cleanUrl, params });
      } else {
        results.push({ url: cleanUrl, params: {} });
      }
    } catch (e) {
      // Игнорируем невалидные URL
    }
  });

  return results;
};

export const applyTemplate = (template, params) => {
  if (!template) return '';
  let result = template;
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(new RegExp(`{${key}}`, 'g'), value);
  }
  return result;
};

const loadImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Image load failed'));
  img.src = url;
});

export const loadFont = async (fontFamily, customFontUrl, customFontName) => {
  if (fontFamily === 'Custom' && customFontUrl && customFontName) {
    try {
      const font = new FontFace(customFontName, `url(${customFontUrl})`);
      await font.load();
      document.fonts.add(font);
      return customFontName;
    } catch (e) {
      console.error("Error loading custom font:", e);
      return 'sans-serif';
    }
  }

  const googleFonts = ['Oswald', 'Inter', 'Roboto', 'Montserrat', 'Open Sans', 'Playfair Display', 'Caveat', 'Rubik', 'PT Sans', 'Lora', 'Comfortaa', 'Russo One'];
  if (googleFonts.includes(fontFamily)) {
    const url = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/ /g, '+')}:wght@400;700&display=swap&subset=cyrillic,cyrillic-ext`;
    if (!document.querySelector(`link[href="${url}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      document.head.appendChild(link);
    }
    try {
      await document.fonts.load(`bold 10px "${fontFamily}"`);
    } catch (e) {
      console.warn("Font loading wait failed, continuing anyway", e);
    }
  }
  return fontFamily;
};

export const createStickerCanvas = async (item, options) => {
  const { url, params } = item;
  const { 
    qrColor = '#000000', 
    bgColor = '#ffffff',
    textColor = '#000000',
    qrBgColor = '#ffffff',
    dotsStyle = 'square', 
    logoUrl,
    stickerWidth = 50,
    stickerHeight = 50,
    stickerRadius = 0,
    topTemplate = '',
    bottomTemplate = '',
    fontSize = 46,
    fontFamily = 'Inter',
    customFontUrl = null,
    customFontName = '',
    logoBgColor = '#000000',
    logoBgOpacity = 80,
    logoBgRadius = 50,
    logoSize = 30
  } = options;

  const activeFont = await loadFont(fontFamily, customFontUrl, customFontName);

  const canvas = document.createElement('canvas');
  const baseWidth = 900; 
  const ratio = stickerHeight / stickerWidth;
  const baseHeight = Math.round(baseWidth * ratio);
  
  canvas.width = baseWidth;
  canvas.height = baseHeight;
  const ctx = canvas.getContext('2d');

  // 1. Фон
  ctx.save();
  const radiusPx = (stickerRadius / stickerWidth) * baseWidth;
  const maxRadius = Math.min(baseWidth, baseHeight) / 2;
  const safeRadiusPx = Math.min(radiusPx, maxRadius);

  ctx.beginPath();
  ctx.roundRect(0, 0, baseWidth, baseHeight, safeRadiusPx);
  ctx.clip();
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, baseWidth, baseHeight);
  ctx.restore();

  const topText = applyTemplate(topTemplate, params);
  const bottomText = applyTemplate(bottomTemplate, params);

  const padding = 40;
  const textLineHeight = fontSize * 1.6;
  const topTextSpace = topText ? textLineHeight : 0;
  const bottomTextSpace = bottomText ? textLineHeight : 0;
  
  const availableQrHeight = baseHeight - topTextSpace - bottomTextSpace - (padding * 2);
  const availableQrWidth = baseWidth - (padding * 2);
  const qrSize = Math.min(availableQrWidth, availableQrHeight);

  // 2. QR-код
  const qrConfig = {
    width: qrSize,
    height: qrSize,
    data: url,
    margin: 0,
    qrOptions: { errorCorrectionLevel: 'H' },
    dotsOptions: { color: qrColor, type: dotsStyle },
    backgroundOptions: { color: qrBgColor }
  };

  const qr = new QRCodeStyling(qrConfig);
  const blob = await qr.getRawData('png');
  const qrImg = await loadImage(URL.createObjectURL(blob));

  // 3. Отрисовка
  ctx.fillStyle = textColor;
  ctx.font = `bold ${fontSize}px "${activeFont}", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  let currentY = padding;

  if (topText) {
    ctx.fillText(topText, baseWidth / 2, currentY + textLineHeight / 2);
    currentY += topTextSpace;
  }

  const qrX = (baseWidth - qrSize) / 2;
  const qrY = currentY + (availableQrHeight - qrSize) / 2; 
  ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
  URL.revokeObjectURL(qrImg.src);

  // 4. Логотип
  if (logoUrl && logoUrl.trim() !== '') {
    try {
      const img = await loadImage(logoUrl);
      const lSize = qrSize * (logoSize / 100);
      const substrateSize = lSize * 1.35;
      const subX = qrX + (qrSize - substrateSize) / 2;
      const subY = qrY + (qrSize - substrateSize) / 2;
      const subRadius = substrateSize * (logoBgRadius / 100);

      ctx.save();
      ctx.globalAlpha = logoBgOpacity / 100;
      ctx.fillStyle = logoBgColor;
      ctx.beginPath();
      ctx.roundRect(subX, subY, substrateSize, substrateSize, subRadius);
      ctx.fill();
      ctx.restore();

      const imgRatio = img.width / img.height;
      let drawW, drawH;
      if (imgRatio > 1) {
        drawW = lSize;
        drawH = lSize / imgRatio;
      } else {
        drawW = lSize * imgRatio;
        drawH = lSize;
      }
      const imgX = qrX + (qrSize - drawW) / 2;
      const imgY = qrY + (qrSize - drawH) / 2;
      ctx.drawImage(img, imgX, imgY, drawW, drawH);
    } catch (e) {
      console.error("Failed to draw logo:", e);
    }
  }

  // 5. Нижний текст (обязательно восстанавливаем цвет)
  ctx.fillStyle = textColor;
  ctx.font = `bold ${fontSize}px "${activeFont}", sans-serif`;
  if (bottomText) {
    const bottomTextY = baseHeight - padding - textLineHeight / 2;
    ctx.fillText(bottomText, baseWidth / 2, bottomTextY);
  }

  return canvas;
};

const processBatch = async (items, options, onProgress) => {
  const results = [];
  for (let i = 0; i < items.length; i++) {
    const canvas = await createStickerCanvas(items[i], options);
    results.push({ canvas, item: items[i] });

    if (i % 5 === 0 || i === items.length - 1) {
      onProgress(Math.round(((i + 1) / items.length) * 100));
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }
  return results;
};

export const exportToZip = async (items, options, onProgress) => {
  const zip = new JSZip();
  const canvases = await processBatch(items, options, onProgress);
  
  // Хранилище для отслеживания дубликатов имен
  const nameCounters = {};

  canvases.forEach(({ canvas, item }, index) => {
    const dataUrl = canvas.toDataURL('image/png');
    const base64Data = dataUrl.replace(/^data:image\/(png|jpg);base64,/, '');
    
    // Формируем имя: кинотеатр-зал-ряд-место
    const { cinema, hall, row, seat } = item.params;
    let baseName = [cinema, hall, row, seat].filter(Boolean).join('-');
    
    // Если параметров нет совсем, используем индекс
    if (!baseName) baseName = `sticker-${index}`;
    
    let finalName = baseName;
    
    // Проверка на дубликаты
    if (nameCounters[baseName] !== undefined) {
      nameCounters[baseName]++;
      finalName = `${baseName}(${nameCounters[baseName]})`;
    } else {
      nameCounters[baseName] = 0;
    }
    
    const filename = `${finalName}.png`;
    zip.file(filename, base64Data, { base64: true });
  });

  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, 'stickers-heroes.zip');
};

export const exportToPdf = async (items, options, onProgress) => {
  const canvases = await processBatch(items, options, onProgress);
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  
  const stickerW = options.stickerWidth; 
  const stickerH = options.stickerHeight; 
  const pageWidth = 210;
  const pageHeight = 297;
  const marginX = 10;
  const marginY = 10;
  const gap = 2; 

  const cols = Math.floor((pageWidth - marginX * 2 + gap) / (stickerW + gap));
  const rows = Math.floor((pageHeight - marginY * 2 + gap) / (stickerH + gap));
  const itemsPerPage = cols * rows;

  if (itemsPerPage === 0) {
    alert("Размер наклейки слишком большой!");
    return;
  }

  const totalGridWidth = cols * stickerW + (cols - 1) * gap;
  const totalGridHeight = rows * stickerH + (rows - 1) * gap;
  const startX = (pageWidth - totalGridWidth) / 2;
  const startY = (pageHeight - totalGridHeight) / 2;

  canvases.forEach(({ canvas }, index) => {
    if (index > 0 && index % itemsPerPage === 0) pdf.addPage();
    const pageIndex = index % itemsPerPage;
    const col = pageIndex % cols;
    const row = Math.floor(pageIndex / cols);
    const x = startX + col * (stickerW + gap);
    const y = startY + row * (stickerH + gap);

    const whiteCanvas = document.createElement('canvas');
    whiteCanvas.width = canvas.width;
    whiteCanvas.height = canvas.height;
    const wCtx = whiteCanvas.getContext('2d');
    wCtx.fillStyle = '#ffffff';
    wCtx.fillRect(0, 0, whiteCanvas.width, whiteCanvas.height);
    wCtx.drawImage(canvas, 0, 0);

    const dataUrl = whiteCanvas.toDataURL('image/jpeg', 0.95);
    pdf.addImage(dataUrl, 'JPEG', x, y, stickerW, stickerH);
  });

  pdf.save('stickers-print.pdf');
};